
import React from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, AlertTriangle } from "lucide-react";
import { Shop } from "@/components/dashboard/shops/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useShopDeletion } from "../hooks/useShopDeletion";

interface DeleteShopButtonProps {
  shop: Shop;
  fullWidth?: boolean;
}

export const DeleteShopButton: React.FC<DeleteShopButtonProps> = ({ shop, fullWidth }) => {
  const {
    isDeleting,
    confirmationText,
    setConfirmationText,
    isConfirmDialogOpen,
    openConfirmDialog,
    closeConfirmDialog,
    handleDeleteShop,
    expectedConfirmationText
  } = useShopDeletion(shop);

  return (
    <>
      <Button 
        variant="destructive" 
        size="sm"
        className={`${fullWidth ? 'w-full' : ''} flex items-center gap-2`}
        onClick={openConfirmDialog}
      >
        <Trash2 className="h-4 w-4" />
        Supprimer la boutique
      </Button>

      <AlertDialog open={isConfirmDialogOpen} onOpenChange={closeConfirmDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Supprimer définitivement cette boutique ?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Cette action est <strong>irréversible</strong>. Toutes les données liées à cette boutique seront définitivement supprimées, y compris:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Tous les produits</li>
                <li>Toutes les commandes</li>
                <li>Tous les paramètres de la boutique</li>
                <li>Toutes les statistiques</li>
              </ul>
              <div className="pt-2">
                <Label htmlFor="confirmation-text" className="text-sm font-medium text-red-600">
                  Pour confirmer, tapez <strong>{expectedConfirmationText}</strong>
                </Label>
                <Input
                  id="confirmation-text"
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={expectedConfirmationText}
                  className="mt-1 border-red-300 focus:border-red-500 focus:ring-red-500"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteShop();
              }}
              disabled={isDeleting || confirmationText !== expectedConfirmationText}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? "Suppression..." : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
